import { useQuery } from '@tanstack/react-query'
import { groupSoundscapes, soundscapesQueryOptions } from '@/entities/soundscape'
import { cn } from '@/shared/lib'
import { FilterTab } from '@/shared/ui/filter-tab'
import { Icon } from '@/shared/ui/icon'
import { currentGroup } from '../lib/catalog'
import { useSoundscapePlayer } from '../model/player-store'
import { useResumePlay } from '../model/use-resume-play'

// Кружок режима: круг — единственная кликабельная зона, подпись под ним.
// Активный кружок держит акцент и пульсирует, пока поток играет.
function Dial({
  slug,
  title,
  active,
  loading,
  onClick,
}: {
  slug: string
  title: string
  active: boolean
  loading: boolean
  onClick: () => void
}) {
  return (
    // Ширина — доля ряда, а не фикс: раздел любого размера ложится в одну
    // строку, кружки просто становятся мельче. Пол ширины — 2rem, ниже мишень
    // уже не нажимается пальцем.
    <li className="flex min-w-8 flex-1 basis-0 flex-col items-center gap-1.5">
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        aria-label={active ? `Остановить «${title}»` : `Включить «${title}»`}
        data-slug={slug}
        className={cn(
          'flex aspect-square w-full max-w-14 items-center justify-center rounded-full border transition',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          active
            ? 'border-accent bg-accent/15 text-accent'
            : 'border-border text-text-tertiary hover:border-text-tertiary hover:text-text-secondary',
        )}
      >
        {/* Отдельной иконки загрузки в наборе нет — состояние «поднимаем поток»
            показываем пульсацией самой кнопки. */}
        <Icon
          name={active ? 'pause' : 'play'}
          size={18}
          className={cn('shrink-0', loading && 'animate-pulse')}
        />
      </button>
      <span
        className={cn(
          'line-clamp-2 w-full text-center text-[11px] leading-tight break-words hyphens-auto',
          active ? 'text-text-primary' : 'text-text-tertiary',
        )}
      >
        {title}
      </span>
    </li>
  )
}

// Громкость стоит на виду, а не в настройках: других органов управления звуком
// у плеера нет, и тихий поток иначе нечем спасти.
function VolumeControl() {
  const { volume, setVolume } = useSoundscapePlayer()
  return (
    <label className="flex items-center gap-2 text-text-tertiary">
      <Icon name="music" size={14} aria-hidden />
      <span className="sr-only">Громкость</span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={volume}
        onChange={(e) => setVolume(Number(e.target.value))}
        aria-label="Громкость"
        className="range-thin w-24"
      />
      <span data-numeric className="mono-num w-8 text-right text-[11px]">
        {Math.round(volume * 100)}%
      </span>
    </label>
  )
}

// Выбор режима Endel. Каталог большой (32 записи), поэтому модальности — разделы,
// а не ярусы: на экране всегда один ряд кружков, и страница фокуса умещается
// целиком. Раздел с играющим режимом помечен точкой — уход в другой раздел не
// прячет то, что сейчас звучит.
export function SoundscapeDials() {
  const { data, isLoading } = useQuery(soundscapesQueryOptions)
  const { slug: activeSlug, playing, loading, error, group, setGroup } = useSoundscapePlayer()
  const resume = useResumePlay()

  if (isLoading) {
    return <p className="text-sm text-text-tertiary">Загрузка режимов…</p>
  }

  const groups = groupSoundscapes(data ?? [])
  if (groups.length === 0) {
    return <p className="text-sm text-text-tertiary">Режимы Endel не настроены</p>
  }

  // Раздел общий с мини-плеером шапки: что переключаешь здесь — то переключается
  // и там. Пока не выбран руками, его задаёт играющий режим.
  const playingKey = groups.find((g) => g.items.some((s) => s.slug === activeSlug))?.key
  const current = currentGroup(groups, group, activeSlug)

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex items-center gap-3">
        <span className="label">Endel</span>
        <span className="hairline flex-1" />
        <VolumeControl />
      </div>

      <div className="flex flex-wrap items-center gap-5 border-b border-border-subtle pb-px">
        {groups.map((g) => (
          <FilterTab key={g.key} active={g.key === current?.key} onClick={() => setGroup(g.key)}>
            <span className="inline-flex items-center gap-1.5">
              {g.title}
              {g.key === playingKey && playing && (
                <span aria-hidden className="size-1.5 rounded-pill bg-accent" />
              )}
            </span>
          </FilterTab>
        ))}
      </div>

      {/* Раздел всегда одной строкой: ни прокрутки вбок, ни переноса — кружки
          делят ширину поровну и мельчают, когда режимов много. */}
      <ul
        aria-label={`Режимы: ${current?.title ?? ''}`}
        className="flex w-full items-start justify-center gap-2 pb-1"
      >
        {(current?.items ?? []).map((s) => (
          <Dial
            key={s.slug}
            slug={s.slug}
            title={s.title}
            active={activeSlug === s.slug && playing}
            loading={activeSlug === s.slug && loading}
            onClick={() => resume.toggle(s.slug, s.title)}
          />
        ))}
      </ul>

      {/* Молчащий плеер обязан назвать причину: отказ браузера и обрыв потока
          иначе выглядят одинаково — просто тишиной. */}
      {error && (
        <p role="alert" className="text-xs text-accent">
          {error}
        </p>
      )}
    </div>
  )
}
